import { useState, useEffect, useCallback } from 'react'
import { sendToApi } from 'api/client'
import { usePollAISuggestionRequest } from 'hooks/queries/aiSuggestionRequests/usePollAISuggestionRequest'
import { SuggestionRequestStatus } from 'types'
import { MediaFile } from './VisualStylePreviewCarousel'

interface GenerateResponse {
  ai_suggestion_request_id: number
}

const MAX_RETRIES = 2

/**
 * Hook to handle generating and polling for a single styled image
 */
export const useStyledImage = (
  mediaFile: MediaFile | undefined,
  visualStyleId: number | null,
  brandKitId: number | undefined,
  triggerManually: boolean = false,
) => {
  const [requestId, setRequestId] = useState<number | null>(null)
  const [styledUrl, setStyledUrl] = useState<string | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  // Track the inputs that triggered the current request to detect changes
  const [currentInputKey, setCurrentInputKey] = useState<string | null>(null)

  const inputKey = mediaFile && visualStyleId && brandKitId ? `${mediaFile.id}-${visualStyleId}-${brandKitId}` : null

  // Poll for the styled image result
  const { data: requestData } = usePollAISuggestionRequest({ id: requestId || 0 }, { enabled: !!requestId })

  const status = requestData?.request?.status
  const isComplete = status === SuggestionRequestStatus.Successful
  const isFailed = status === SuggestionRequestStatus.Failed || status === SuggestionRequestStatus.Aborted

  // Extract the styled URL when polling completes
  useEffect(() => {
    if (isComplete && requestData?.request?.generated_content) {
      try {
        const result = JSON.parse(requestData.request.generated_content)
        if (result.source_url) {
          setStyledUrl(result.source_url)
        }
      } catch {
        // Ignore parse errors
      }
    }
  }, [isComplete, requestData?.request?.generated_content])

  const triggerGeneration = useCallback(async () => {
    if (!mediaFile || !visualStyleId || !brandKitId) {
      return
    }

    setIsGenerating(true)

    // Increment retry count if this is a retry
    if (isFailed) {
      setRetryCount((prev) => prev + 1)
    }

    try {
      const response = await sendToApi<GenerateResponse>(`/visual_styles/${visualStyleId}/generate`, {
        method: 'POST',
        body: {
          source_url: mediaFile.url,
          brand_kit_id: brandKitId,
          aspect_ratio: '3:4',
        },
      })

      if (response.type === 'success' && response.data.ai_suggestion_request_id) {
        setRequestId(response.data.ai_suggestion_request_id)
      }
    } catch {
      // Ignore errors
    } finally {
      setIsGenerating(false)
    }
  }, [mediaFile, visualStyleId, brandKitId, isFailed])

  // Trigger generation when inputs change or on retry after failure
  useEffect(() => {
    // Clear state if inputs are missing
    if (!inputKey || !mediaFile || !visualStyleId || !brandKitId) {
      setRequestId(null)
      setStyledUrl(null)
      setRetryCount(0)
      setCurrentInputKey(null)
      return
    }

    // If inputs changed, reset and start fresh
    if (inputKey !== currentInputKey) {
      setRequestId(null)
      setStyledUrl(null)
      setRetryCount(0)
      setCurrentInputKey(inputKey)
    }

    // Skip if we already have a successful result for current inputs
    if (styledUrl && inputKey === currentInputKey) {
      return
    }

    // Skip if request is in progress
    if (requestId && !isFailed) {
      return
    }

    // If failed and we've exhausted retries, stop
    if (isFailed && retryCount >= MAX_RETRIES) {
      return
    }

    if (!triggerManually) triggerGeneration()
  }, [
    inputKey,
    currentInputKey,
    mediaFile,
    visualStyleId,
    brandKitId,
    requestId,
    isFailed,
    styledUrl,
    retryCount,
    triggerGeneration,
    triggerManually,
  ])

  const isLoading = isGenerating || (!!requestId && !isComplete && !isFailed) || (isFailed && retryCount < MAX_RETRIES)

  // Track if we've exhausted all retries
  const hasFailed = isFailed && retryCount >= MAX_RETRIES && !styledUrl

  return { styledUrl, isLoading, hasFailed, triggerGeneration }
}
